/*
 * File: DeliveryAddressCard.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 30 Dec 2021 9:56:22 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Card, Col, Row, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { DeliveryAddressModel } from 'models/DeliveryAddressModel';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React from 'react';
import { useLocation } from 'react-router-dom';

/* ****************** Interfaces ****************** */
interface DeliveryAddressCardProps {
  deliveryAddress: DeliveryAddressModel;
}

const styles: InlineStylesModel = {
  cardBody: {
    borderRadius: 10,
    padding: 0
  },
  customerInfo: {
    padding: '10px 75px',
    paddingRight: 20
  },
  title: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(0, 0, 0, .4)'
  },
  titleWhite: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(255, 255, 255, .4)'
  },
  values: {
    fontSize: 16,
    fontWeight: 600
  },
  valuesWhite: {
    color: 'rgba(255, 255, 255, .9)',
    fontSize: 16,
    fontWeight: 600
  },
  minusButton: {
    color: '#F93943',
    borderColor: '#F93943'
  },
  selectedStyles: {
    transition: '.25s all ease-out',
    background: '#4e937aab',
    boxShadow: '0 0 6px rgba(0,0,0,.4)',
    borderRadius: 10
  }
};

export const DeliveryAddressCard = ({ deliveryAddress }: DeliveryAddressCardProps): JSX.Element => {
  const { values, setValues } = useFormikContext<SalesOrderPayloadModel>();
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Functions ****************** */
  const handleAddAddress = (): void => {
    if (values.deliveryAddressLocationId !== deliveryAddress.locationId) {
      setValues((prevState) => ({
        ...prevState,
        deliveryAddressStreet: deliveryAddress.street || '',
        deliveryAddressCity: deliveryAddress.city || '',
        deliveryAddressCountry: deliveryAddress.country || '',
        deliveryAddressCounty: deliveryAddress.county || '',
        deliveryAddressState: deliveryAddress.state || '',
        deliveryAddressZipCode: deliveryAddress.zipCode || '',
        deliveryAddressDescription: deliveryAddress.description || '',
        deliveryAddressLocationId: deliveryAddress.locationId
      }));
    }
  };

  const handleRemoveAddress = (): void => {
    if (values.deliveryAddressLocationId) {
      setValues((prevState) => ({
        ...prevState,
        deliveryAddressStreet: '',
        deliveryAddressCity: '',
        deliveryAddressCountry: '',
        deliveryAddressCounty: '',
        deliveryAddressState: '',
        deliveryAddressZipCode: '',
        deliveryAddressDescription: '',
        deliveryAddressLocationId: ''
      }));
    }
  };

  /* ****************** JSX / Variables ****************** */

  const cardStyle = values.deliveryAddressLocationId ? { ...styles.selectedAddress, ...styles.cardBody } : styles.cardBody;

  const isSelectedAddress = values.deliveryAddressLocationId === deliveryAddress.locationId;

  const unnamedDescriptionJSX = !deliveryAddress.description && <Typography.Paragraph style={isSelectedAddress ? styles.valuesWhite : styles.values}>N/A</Typography.Paragraph>;

  const descriptionJSX = deliveryAddress.description ? (
    <Typography.Paragraph style={isSelectedAddress ? styles.valuesWhite : styles.values} ellipsis={{ tooltip: deliveryAddress.description }}>
      {deliveryAddress.description}
    </Typography.Paragraph>
  ) : (
    unnamedDescriptionJSX
  );

  const unavailableCityJSX = !deliveryAddress.city && (
    <Typography.Paragraph>
      {deliveryAddress.state} {deliveryAddress.zipCode} {deliveryAddress.country}
    </Typography.Paragraph>
  );
  const formattedAddressJSX = deliveryAddress.city ? (
    <Typography.Paragraph style={isSelectedAddress ? styles.valuesWhite : styles.values}>
      {deliveryAddress.street}, {deliveryAddress.city}, {deliveryAddress.state} {deliveryAddress.zipCode}
      {deliveryAddress.country}
    </Typography.Paragraph>
  ) : (
    unavailableCityJSX
  );

  const Address = (): JSX.Element => {
    return (
      <Typography.Paragraph style={{ color: 'white' }}>
        {deliveryAddress.street}, {deliveryAddress.city}, {deliveryAddress.state} {deliveryAddress.zipCode}
        {deliveryAddress.country}
      </Typography.Paragraph>
    );
  };

  /* ****************** Render ****************** */
  return (
    <Card
      bordered={false}
      style={isSelectedAddress ? styles.selectedStyles : cardStyle}
      bodyStyle={styles.cardBody}
      onClick={isPosted ? undefined : isSelectedAddress ? handleRemoveAddress : handleAddAddress}
      hoverable={!isPosted}>
      <Row style={{ borderRadius: 10 }}>
        <Col span={5} style={{ background: isSelectedAddress ? '#4e937a' : 'rgba(0,0,0,.2)', padding: '10px 20px', borderRadius: '10px 0 0 10px' }}>
          <Typography.Paragraph style={isSelectedAddress ? styles.titleWhite : styles.title}>Name</Typography.Paragraph>
          <Typography.Paragraph style={isSelectedAddress ? styles.valuesWhite : styles.values} ellipsis={{ tooltip: `${descriptionJSX}` }}>
            {descriptionJSX}
          </Typography.Paragraph>
        </Col>
        <Col span={19}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '97.25px',
              height: '48.625px',
              borderLeft: isSelectedAddress ? 'solid 48.625px #4e937a' : 'solid 48.625px rgba(0,0,0,.2)',
              borderBottom: 'solid 48.625px transparent',
              borderTop: 'solid 48.625px transparent'
            }}
          />
          <Col style={styles.customerInfo}>
            <Typography.Paragraph style={isSelectedAddress ? styles.titleWhite : styles.title}>Address</Typography.Paragraph>
            <Typography.Paragraph style={isSelectedAddress ? styles.valuesWhite : styles.values} ellipsis={{ tooltip: <Address /> }}>
              {formattedAddressJSX}
            </Typography.Paragraph>
          </Col>
        </Col>
      </Row>
    </Card>
  );
};
